import { ChevronDown, ChevronRight } from 'lucide-react'
import {
  cloneElement,
  createContext,
  isValidElement,
  useContext,
  type HTMLAttributes,
  type MouseEvent,
  type ReactElement,
  type ReactNode,
} from 'react'

import { cx } from './core/classes.js'
import { mergeRenderProps } from './core/render.js'

type Variant = 'pane' | 'surface'
type Dividers = 'full' | 'inset'

const Context = createContext<{ dividers: Dividers; variant: Variant } | undefined>(undefined)

/** Grouped rows with dividers. */
export function Rows(props: Rows.Props) {
  const { children, className, dividers: dividersProp, variant = 'pane', ...rest } = props
  const dividers = dividersProp ?? Rows.defaultDividers[variant]

  return (
    <Context.Provider value={{ dividers, variant }}>
      <div
        className={cx(
          Rows.className[variant],
          Rows.dividersClassName[variant][dividers],
          className,
        )}
        {...rest}
      >
        {children}
      </div>
    </Context.Provider>
  )
}

export namespace Rows {
  /** Props for {@link Rows}. */
  export type Props = HTMLAttributes<HTMLDivElement> & {
    /** Row items. */
    children: ReactNode
    /** Horizontal row divider length. Defaults to `'inset'` for pane rows and `'full'` for surface rows. @default Rows.defaultDividers[variant] */
    dividers?: Dividers | undefined
    /** Visual row group surface. @default 'pane' */
    variant?: Variant | undefined
  }

  /** Horizontal row divider length. */
  export type Dividers = 'full' | 'inset'

  /** Visual row group surface. */
  export type Variant = 'pane' | 'surface'

  /** Indented nested rows inside a row list. Use with expand rows and virtualized flat row lists. */
  export function Group(props: Group.Props) {
    const { children, className, ...rest } = props
    const variant = useContext(Context)?.variant ?? 'pane'
    return (
      <div className={cx(Group.className[variant], className)} {...rest}>
        {children}
      </div>
    )
  }

  export namespace Group {
    /** Props for nested rows rendered with `Rows.Group`. */
    export type Props = HTMLAttributes<HTMLDivElement> & {
      /** Nested row items. */
      children: ReactNode
    }

    /** Classes for the nested row group wrapper. */
    export const className = {
      pane: 'relative [--rows-pane-content-padding-left:20px] [--rows-row-padding-left:20px] before:pointer-events-none before:absolute before:inset-y-0 before:left-[10px] before:z-10 before:w-[2px] before:bg-border [&_[data-rows-divider]]:left-[var(--rows-group-divider-left)]',
      surface:
        'relative [--rows-pane-content-padding-left:48px] [--rows-row-padding-left:48px] before:pointer-events-none before:absolute before:inset-y-0 before:left-[28px] before:z-10 before:w-[2px] before:bg-border [&_[data-rows-divider]]:left-[var(--rows-group-divider-left)]',
    } as const satisfies Record<Variant, string>
  }

  /** Expanded panel content inside a row list. Use for arbitrary content that is not made of nested rows. */
  export function Panel(props: Panel.Props) {
    const { children, className, ...rest } = props
    const variant = useContext(Context)?.variant ?? 'pane'

    return (
      <div className={cx(Panel.className[variant], className)} {...rest}>
        {children}
      </div>
    )
  }

  export namespace Panel {
    /** Props for expanded content rendered with `Rows.Panel`. */
    export type Props = HTMLAttributes<HTMLDivElement> & {
      /** Panel content. */
      children: ReactNode
    }

    /** Classes for each expanded panel surface. */
    export const className = {
      pane: 'overflow-hidden border-t border-border-pane bg-pane',
      surface: 'overflow-hidden border-t border-border bg-pane',
    } as const satisfies Record<Variant, string>
  }

  /** Classes for each row group surface. */
  export const className = {
    pane: 'overflow-hidden rounded-body bg-pane px-[14px] [--rows-divider:var(--border-color-border-pane)] [--rows-pane-content-padding-left:0px] [&_[data-rows-divider]]:bg-[var(--rows-divider)]',
    surface:
      'overflow-hidden bg-surface [--rows-divider:var(--border-color-border)] [&_[data-rows-divider]]:bg-[var(--rows-divider)]',
  } as const satisfies Record<Variant, string>

  /** Classes for each row divider length. */
  export const dividersClassName = {
    pane: {
      full: '[--rows-group-divider-left:0px]',
      inset: '[--rows-group-divider-left:0px]',
    },
    surface: {
      full: '[--rows-group-divider-left:0px]',
      inset: '[--rows-group-divider-left:20px]',
    },
  } as const satisfies Record<Variant, Record<Dividers, string>>

  /** Default divider length for each row surface. */
  export const defaultDividers = {
    pane: 'inset',
    surface: 'full',
  } as const satisfies Record<Variant, Dividers>

  /** A row inside `<Rows>`. Supports compact key-value rows, full content rows, navigation, and expand affordances. */
  export function Row(props: Row.Props) {
    const {
      action = { type: 'none' },
      children,
      className,
      disabled,
      height,
      label,
      onClick,
      render,
      spacing = 'both',
      style,
      ...rest
    } = props
    const context = useContext(Context)
    const variant = context?.variant ?? (label === undefined ? 'surface' : 'pane')
    const dividers = context?.dividers ?? Rows.defaultDividers[variant]
    const type = action.type
    const detailsContent = type === 'details' ? action.content : undefined
    const expanded = (() => {
      if (type === 'expand') return action.expanded
      if (type === 'details') return detailsContent !== undefined && detailsContent !== false
      return false
    })()
    const panel = detailsContent === true ? <Rows.PanelLoader /> : detailsContent
    const content = renderContent({ children, expanded, label, type, variant })
    const interactive = Boolean(onClick || render)
    const rowStyle = { height: height ?? Row.height[variant], ...style }
    const rowClassName = cx(
      Row.baseClassName[variant],
      interactive && Row.interactiveClassName[variant],
      interactive && 'select-none',
      variant === 'pane' &&
        (interactive ? Row.buttonSpacingClassName[spacing] : Row.spacingClassName[spacing]),
      disabled && 'pointer-events-none opacity-50',
      className,
    )
    const dividerClassName = (() => {
      if (variant === 'surface')
        return cx(Row.dividerClassName, Row.surfaceDividerClassName[dividers])

      return cx(
        Row.dividerClassName,
        Row.paneDividerClassName[dividers][interactive ? spacing : 'static'],
      )
    })()
    const ownProps = {
      ...rest,
      'aria-disabled': disabled && interactive ? true : undefined,
      'aria-expanded':
        type === 'expand' ? action.expanded : type === 'details' ? expanded : undefined,
      className: rowClassName,
      'data-disabled': disabled ? '' : undefined,
      ...(onClick ? { onClick } : {}),
      style: rowStyle,
    }

    const row = (() => {
      if (render && isValidElement(render))
        return cloneElement(
          render,
          mergeRenderProps(render.props as Record<string, unknown>, ownProps),
          <>
            <RowDivider className={dividerClassName} />
            {content}
          </>,
        )

      if (onClick)
        return (
          <button {...ownProps} disabled={disabled} type="button">
            <RowDivider className={dividerClassName} />
            {content}
          </button>
        )

      return (
        <div {...ownProps}>
          <RowDivider className={dividerClassName} />
          {content}
        </div>
      )
    })()

    if (type !== 'details') return row

    return (
      <>
        {row}
        {expanded && panel !== undefined && panel !== false && <Rows.Panel>{panel}</Rows.Panel>}
      </>
    )
  }

  export namespace Row {
    /** Row action affordance. */
    export type Action = ActionDetails | ActionExpand | ActionNavigation | ActionNone

    /** Deprecated details row action. Use `ActionExpand` with a sibling `Rows.Panel` instead. @deprecated Use `ActionExpand` with a sibling `Rows.Panel` instead. */
    export type ActionDetails = {
      /** Deprecated details panel content. `false`/`undefined` closes, `true` opens a loader, and content opens. @deprecated Use a sibling `Rows.Panel` instead. */
      content?: DetailsContent | undefined
      /** Deprecated action type. @deprecated Use `type: 'expand'` instead. */
      type: 'details'
    }

    /** Expand row action. Shows a chevron for rows that open adjacent content such as `Rows.Group`. */
    export type ActionExpand = {
      /** Whether the adjacent content is currently open. */
      expanded: boolean
      /** Action type. */
      type: 'expand'
    }

    /** Navigation row action. */
    export type ActionNavigation = {
      /** Action type. */
      type: 'navigation'
    }

    /** No visual row action. */
    export type ActionNone = {
      /** Action type. */
      type: 'none'
    }

    /** Details panel content shorthand. @deprecated Use a sibling `Rows.Panel` instead. */
    export type DetailsContent =
      | Exclude<ReactNode, boolean | null | undefined>
      | boolean
      | undefined

    /** Press handler. */
    export type OnClick = (event: MouseEvent<HTMLElement>) => void

    /** Props for {@link Rows.Row}. */
    export type Props = Omit<HTMLAttributes<HTMLElement>, 'action' | 'onClick'> &
      (
        | {
            /** Row action affordance. @deprecated Use `ActionExpand` with a sibling `Rows.Panel` instead. */
            action: ActionDetails
            /** Press handler. For details rows, update `action.content` from here. @deprecated Use `ActionExpand` with a sibling `Rows.Panel` instead. */
            onClick: OnClick
          }
        | {
            /** Row action affordance. */
            action: ActionExpand
            /** Press handler. Update `action.expanded` and render adjacent content from here. */
            onClick: OnClick
          }
        | {
            /** Row action affordance. */
            action: ActionNavigation
            /** Press handler. */
            onClick: OnClick
          }
        | {
            /** Row action affordance. @default 'none' */
            action?: ActionNone | undefined
            /** Press handler. When provided without `render` or a visual action, the row renders as a button. */
            onClick?: OnClick | undefined
          }
      ) & {
        /** Row content. */
        children: ReactNode
        /** Disable interaction when the row is interactive. */
        disabled?: boolean | undefined
        /** Row height in px. Defaults to the variant height. */
        height?: number | undefined
        /** Left-side label for key-value rows. */
        label?: ReactNode | undefined
        /** Element to render as instead of a div or button. */
        render?: ReactElement | undefined
        /** Which sides keep the parent `<Rows>` padding. Only applies to pane rows. @default 'both' */
        spacing?: Spacing | undefined
      }

    /** Row padding behavior relative to the parent `<Rows>` inset. */
    export type Spacing = 'both' | 'end' | 'none' | 'start'

    /** Classes for each row surface. */
    export const baseClassName = {
      pane: 'group/rows-row relative flex items-center gap-[12px] label-13',
      surface:
        'group/rows-row relative flex w-full min-w-0 items-center gap-[14px] pl-[var(--rows-row-padding-left,20px)] pr-[var(--rows-row-padding-right,20px)] text-left text-foreground',
    } as const satisfies Record<Variant, string>

    /** Classes for the row divider line. */
    export const dividerClassName =
      'pointer-events-none absolute -top-px left-[var(--rows-row-divider-left)] right-[var(--rows-row-divider-right)] h-px'

    /** Classes for pane row divider length. */
    export const paneDividerClassName = {
      full: {
        both: '[--rows-row-divider-left:0px] [--rows-row-divider-right:0px]',
        end: '[--rows-row-divider-left:0px] [--rows-row-divider-right:-14px]',
        none: '[--rows-row-divider-left:0px] [--rows-row-divider-right:0px]',
        start: '[--rows-row-divider-left:-14px] [--rows-row-divider-right:0px]',
        static: '[--rows-row-divider-left:-14px] [--rows-row-divider-right:-14px]',
      },
      inset: {
        both: '[--rows-row-divider-left:14px] [--rows-row-divider-right:14px]',
        end: '[--rows-row-divider-left:14px] [--rows-row-divider-right:0px]',
        none: '[--rows-row-divider-left:14px] [--rows-row-divider-right:14px]',
        start: '[--rows-row-divider-left:0px] [--rows-row-divider-right:14px]',
        static: '[--rows-row-divider-left:0px] [--rows-row-divider-right:0px]',
      },
    } as const satisfies Record<Dividers, Record<Spacing | 'static', string>>

    /** Classes for surface row divider length. */
    export const surfaceDividerClassName = {
      full: '[--rows-row-divider-left:0px] [--rows-row-divider-right:0px]',
      inset: '[--rows-row-divider-left:20px] [--rows-row-divider-right:20px]',
    } as const satisfies Record<Dividers, string>

    /** Default row heights for each surface. */
    export const height = {
      pane: 40,
      surface: 56,
    } as const satisfies Record<Variant, number>

    /** Interactive classes for each row surface. */
    export const interactiveClassName = {
      pane: 'cursor-pointer hover:bg-secondary-hover active:bg-secondary-active focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[-2px] focus-visible:outline-focus',
      surface:
        'cursor-pointer hover:bg-surface-hover active:bg-surface-active focus-visible:outline focus-visible:outline-[2px] focus-visible:outline-offset-[-2px] focus-visible:outline-focus',
    } as const satisfies Record<Variant, string>

    /** Classes that keep or remove parent row insets for static pane rows. */
    export const spacingClassName = {
      both: '',
      end: 'ml-[-14px]',
      none: 'mx-[-14px]',
      start: 'mr-[-14px]',
    } as const satisfies Record<Spacing, string>

    /** Classes that keep or remove parent row insets for pressable pane rows. */
    export const buttonSpacingClassName = {
      both: 'mx-[-14px] w-[calc(100%+28px)] px-[14px]',
      end: 'ml-[-14px] w-[calc(100%+14px)]',
      none: 'mx-[-14px] w-[calc(100%+28px)]',
      start: 'mr-[-14px] w-[calc(100%+14px)]',
    } as const satisfies Record<Spacing, string>
  }

  /** Skeleton panel content for rows that are loading expanded panel content. */
  export function PanelLoader() {
    return (
      <div className="grid gap-[8px] px-[20px] py-[12px] label-13">
        <div className="relative flex items-center justify-between gap-[16px]">
          <span aria-hidden className="invisible">
            Label
          </span>
          <span aria-hidden className="invisible">
            Value
          </span>
          <div className="absolute h-[14px] w-[72px] animate-pulse rounded-body bg-skeleton" />
          <div className="absolute right-0 h-[14px] w-[96px] animate-pulse rounded-body bg-skeleton" />
        </div>
        <div className="relative flex items-center justify-between gap-[16px]">
          <span aria-hidden className="invisible">
            Label
          </span>
          <span aria-hidden className="invisible">
            Value
          </span>
          <div className="absolute h-[14px] w-[64px] animate-pulse rounded-body bg-skeleton" />
          <div className="absolute right-0 h-[14px] w-[72px] animate-pulse rounded-body bg-skeleton" />
        </div>
      </div>
    )
  }

  /** Skeleton group content for rows that are loading expanded groups. */
  export function RowGroupLoader() {
    return (
      <>
        <Rows.Row>
          <span aria-hidden className="size-6 shrink-0 animate-pulse rounded-button bg-skeleton" />
          <div className="grid min-w-0 flex-1 gap-[6px]">
            <span aria-hidden className="h-[14px] w-[128px] rounded-body bg-skeleton" />
            <span aria-hidden className="h-[13px] w-[96px] rounded-body bg-skeleton" />
          </div>
          <span aria-hidden className="h-[13px] w-[36px] shrink-0 rounded-body bg-skeleton" />
        </Rows.Row>
        <Rows.Row>
          <span aria-hidden className="size-6 shrink-0 animate-pulse rounded-button bg-skeleton" />
          <div className="grid min-w-0 flex-1 gap-[6px]">
            <span aria-hidden className="h-[14px] w-[112px] rounded-body bg-skeleton" />
            <span aria-hidden className="h-[13px] w-[88px] rounded-body bg-skeleton" />
          </div>
          <span aria-hidden className="h-[13px] w-[28px] shrink-0 rounded-body bg-skeleton" />
        </Rows.Row>
      </>
    )
  }
}

/** @deprecated Use `Rows.Row` instead. */
export function Row(props: Row.Props) {
  return <Rows.Row {...props} />
}

/** @deprecated Use `Rows.Row` instead. */
export namespace Row {
  /** Props for {@link Row}. @deprecated Use `Rows.Row.Props` instead. */
  export type Props = Rows.Row.Props

  /** Row padding behavior relative to the parent `<Rows>` inset. @deprecated Use `Rows.Row.Spacing` instead. */
  export type Spacing = Rows.Row.Spacing

  /** Classes that keep or remove parent row insets for static rows. @deprecated Use `Rows.Row.spacingClassName` instead. */
  export const spacingClassName = Rows.Row.spacingClassName

  /** Classes that keep or remove parent row insets for pressable rows. @deprecated Use `Rows.Row.buttonSpacingClassName` instead. */
  export const buttonSpacingClassName = Rows.Row.buttonSpacingClassName
}

function ExpandIcon(props: { expanded: boolean }) {
  const { expanded } = props
  return (
    <ChevronDown
      aria-hidden
      className="size-[16px] shrink-0 text-foreground-secondary opacity-70 data-[open]:rotate-180"
      data-open={expanded ? '' : undefined}
    />
  )
}

function RowDivider(props: { className: string }) {
  return <span aria-hidden className={props.className} data-rows-divider="" />
}

function NavigateIcon() {
  return (
    <ChevronRight
      aria-hidden
      className="size-[16px] shrink-0 text-foreground-secondary opacity-70"
    />
  )
}

function renderContent(options: {
  children: ReactNode
  expanded: boolean
  label: ReactNode | undefined
  type: Rows.Row.Action['type']
  variant: Rows.Variant
}) {
  const { children, expanded, label, type, variant } = options
  if (label !== undefined)
    return (
      <>
        <div
          className={cx(
            'grid min-w-0 flex-1 grid-cols-[minmax(0,max-content)_minmax(0,1fr)] items-center gap-[12px]',
            variant === 'pane' && 'pl-[var(--rows-pane-content-padding-left)]',
          )}
        >
          <div className="min-w-0 text-left text-foreground-secondary">{label}</div>
          <div className="flex min-w-0 items-center justify-end text-right">{children}</div>
        </div>
        <ActionIcon expanded={expanded} type={type} />
      </>
    )
  if (variant === 'pane')
    return (
      <>
        <div className="min-w-0 flex-1 pl-[var(--rows-pane-content-padding-left)]">{children}</div>
        <ActionIcon expanded={expanded} type={type} />
      </>
    )
  return (
    <>
      {children}
      <ActionIcon expanded={expanded} type={type} />
    </>
  )
}

function ActionIcon(props: { expanded: boolean; type: Rows.Row.Action['type'] }) {
  const { expanded, type } = props
  if (type === 'details' || type === 'expand') return <ExpandIcon expanded={expanded} />
  if (type === 'navigation') return <NavigateIcon />
  return null
}
